import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Loader2, AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";
import type { Case } from "@shitamy/types";

/**
 * 双方合意による取引削除（sql/33・業者側）。
 * 顧客・業者の両方が同意した時だけ削除される。
 */
export function MutualDeleteBar({ c, onDeleted }: { c: Case; onDeleted?: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const reqMut = useMutation({
    mutationFn: () => api.requestCaseDeletion(c.id),
    onSuccess: (res) => {
      setConfirm(false);
      if (res === "deleted") {
        qc.invalidateQueries({ queryKey: ["my-bids"] });
        onDeleted?.();
      } else {
        qc.invalidateQueries({ queryKey: ["case", c.id] });
      }
    },
  });
  const withdrawMut = useMutation({
    mutationFn: () => api.withdrawCaseDeletion(c.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", c.id] }),
  });

  if (c.status === "paid" || !c.vendor_id) return null;

  const mine = !!c.del_req_vendor;
  const other = !!c.del_req_customer;
  const busy = reqMut.isPending || withdrawMut.isPending;

  return (
    <div className="rounded-xl border border-[var(--color-border)] p-3 space-y-2">
      <p className="text-xs font-bold flex items-center gap-1.5 text-ink-soft dark:text-white/70">
        <Trash2 size={14} /> 取引の削除（双方の同意が必要）
      </p>

      {other && !mine ? (
        <>
          <p className="text-sm">お客様がこの取引の削除を希望しています。同意すると取引と関連データが削除されます。</p>
          <button
            onClick={() => reqMut.mutate()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
          >
            {reqMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} 同意して削除する
          </button>
        </>
      ) : mine ? (
        <>
          <p className="text-sm text-ink-soft dark:text-white/70">削除に同意済みです。お客様の同意を待っています。</p>
          <button
            onClick={() => withdrawMut.mutate()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 border border-[var(--color-border)] disabled:opacity-50 text-sm font-medium py-2.5 rounded-xl"
          >
            {withdrawMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} 削除の同意を取り下げる
          </button>
        </>
      ) : (
        <>
          <p className="text-[11px] text-ink-soft dark:text-white/60">この取引を取りやめる場合、お客様が同意した時点で削除されます。</p>
          <button
            onClick={() => setConfirm(true)}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 border border-red-300 text-red-600 dark:text-red-300 disabled:opacity-50 text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
          >
            <Trash2 size={15} /> この取引の削除を依頼する
          </button>
        </>
      )}

      {(reqMut.error || withdrawMut.error) && (
        <p className="text-xs text-red-600">{((reqMut.error || withdrawMut.error) as Error).message}</p>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6" onClick={() => !busy && setConfirm(false)}>
          <div className="bg-[var(--color-bg)] text-[var(--color-fg)] rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={16} className="text-red-600" /> 取引の削除を依頼しますか？</p>
            <p className="text-sm text-ink-soft dark:text-white/70">
              お客様に削除の同意をお願いします。双方が同意すると、この取引・写真・やり取りは削除され元に戻せません。
            </p>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setConfirm(false)} disabled={busy} className="flex-1 py-2.5 rounded-xl border border-[var(--color-border)] text-sm">やめる</button>
              <button onClick={() => reqMut.mutate()} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold flex items-center justify-center gap-1.5">
                {reqMut.isPending ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} 依頼する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
